import { useCashback } from "@/contexts/cashback";
import { useReservation } from "@/contexts/reservation";
import { useSettings } from "@/contexts/settings";
import { AccumulationResponse } from "@/types/cashback";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useMemo } from "react";

type UseAccumulationResult = UseQueryResult<AccumulationResponse>;

export function useAccumulation(): UseAccumulationResult {
  const { depot, estimate, paymentMethod } = useReservation();
  const { pixDiscount, creditCardDiscount } = useSettings();
  const { isCashback, rescueValue } = useCashback();

  let prepaymentDiscount = 0;

  if (paymentMethod === 'creditCard') {
    prepaymentDiscount = creditCardDiscount;
  } else if (paymentMethod === 'pix') {
    prepaymentDiscount = pixDiscount;
  }

  const discountValue = useMemo(() => {
    if (paymentMethod === 'creditCard') {
      if (estimate && estimate.DAYS) {
        return Math.ceil((estimate.DAYS.total * creditCardDiscount) / 100 * 100) / 100;
      }
    } else if (paymentMethod === 'pix') {
      if (estimate && estimate.DAYS) {
        return Math.ceil((estimate.DAYS.total * pixDiscount) / 100 * 100) / 100;
      }
    }

    return 0;
  }, [paymentMethod, estimate, creditCardDiscount, pixDiscount]);

  const unroundedDiscountedTotalForDailies = useMemo(() => {
    if (estimate && estimate.DAYS) {
      const percentageOff = paymentMethod === 'pix' ? pixDiscount : paymentMethod === 'creditCard' ? creditCardDiscount : 0;
      return estimate.DAYS.total * (1 - (percentageOff / 100));
    }
    return estimate?.DAYS?.total || 0;
  }, [estimate, paymentMethod, pixDiscount, creditCardDiscount]);

  const days = estimate && estimate.DAYS ? parseInt(estimate.DAYS.quantity) : 1;
  const dailyPrice = estimate && estimate.DAYS ? estimate.DAYS.total / days : 0;

  const body = {
    storeId: depot?.coral_code,
    days,
    dailyPrice,
    reservationCreatedDate: new Date().toISOString(),
    couponDiscount: estimate?.DISCNT?.total ? Math.abs(estimate.DISCNT.total + discountValue) : 0,
    prepaymentDiscount: estimate?.DAYS?.total ? estimate.DAYS.total - unroundedDiscountedTotalForDailies : 0,
    cashbackDiscount: isCashback && rescueValue > 0 ? rescueValue : 0,
    isCalculate: true,
  }

  const { data, ...query } = useQuery({
    queryKey: ['cashback', 'accumulation', paymentMethod, estimate],
    queryFn: async () => {
      const response = await fetch(`/api/cashback/accumulation`, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        return {
          success: false,
        }
      }

      return response.json();
    }
  });

  return {
    data,
    ...query,
  }
}
